const inrFmt = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 2 });
const TZ = 'Asia/Kolkata';

export const inr = (paise: number | null | undefined) => (paise == null ? '—' : inrFmt.format(paise / 100));
const inrPaiseFmt = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** ₹1,200 for whole rupees, ₹1,345.20 otherwise (never "₹1,345.2"): for amounts that carry paise, like a priced extension. */
export const inrPaise = (paise: number | null | undefined) => (paise == null ? '—' : paise % 100 === 0 ? inr(paise) : inrPaiseFmt.format(paise / 100));
export const rupeesToPaise = (r: string | number) => Math.round(Number(r) * 100);
export const paiseToRupees = (p: number) => (p / 100).toString();

/**
 * Points (the owner's points system: the customer pays rupees at the reception and gets that many points, 1 point = ₹1
 * = 100 paise; the server keeps the balance in paise in the closed-loop wallet). 100000 → "1,000"; 12050 → "120.5".
 */
const pointsFmt = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 });
export const points = (paise: number | null | undefined) => (paise == null || !Number.isFinite(paise) ? '—' : pointsFmt.format(paise / 100));
/** "1,000 points", "1 point". */
export const pointsText = (paise: number | null | undefined) => (paise == null || !Number.isFinite(paise) ? '—' : `${points(paise)} ${Math.abs(paise) === 100 ? 'point' : 'points'}`);

export function dateTime(v: string | Date | null | undefined) {
  if (!v) return '—';
  return new Intl.DateTimeFormat('en-IN', { timeZone: TZ, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(v));
}

export function date(v: string | Date | null | undefined) {
  if (!v) return '—';
  const d = typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(`${v}T12:00:00+05:30`) : new Date(v);
  return new Intl.DateTimeFormat('en-IN', { timeZone: TZ, day: '2-digit', month: 'short', year: 'numeric' }).format(d);
}

export function time(v: string | Date | null | undefined) {
  if (!v) return '—';
  return new Intl.DateTimeFormat('en-IN', { timeZone: TZ, hour: '2-digit', minute: '2-digit' }).format(new Date(v));
}

export function duration(minutes: number | null | undefined) {
  if (minutes == null) return '—';
  const d = Math.floor(minutes / 1440);
  const h = Math.floor((minutes % 1440) / 60);
  const m = minutes % 60;
  return [d && `${d}d`, h && `${h}h`, `${m}m`].filter(Boolean).join(' ');
}

/** Today's business date (YYYY-MM-DD) in Asia/Kolkata. */
export const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());

export function addDays(d: string, n: number) {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
}

/** datetime-local input value in IST for a Date. */
export function toLocalInput(d: Date) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(d);
  const g = (t: string) => parts.find((p) => p.type === t)?.value;
  return `${g('year')}-${g('month')}-${g('day')}T${g('hour')}:${g('minute')}`;
}

/** Parses a datetime-local value as IST. */
export const fromLocalInput = (v: string) => new Date(`${v}:00+05:30`).toISOString();

export const titleCase = (s: string | null | undefined) => (s ?? '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const ROLE_LABELS: Record<string, string> = {
  owner: 'Super admin',
  accountant: 'Accountant',
  security: 'Gate security',
  // The cashier works the reception: loads customers' wallets, gives the app setup code, sees the gate board.
  cashier: 'Reception cashier',
  shop_admin: 'Shop admin',
  shop_staff: 'Shop staff',
  venue_staff: 'Venue staff',
};
export const roleLabel = (role: string) => ROLE_LABELS[role] ?? titleCase(role);
